// ============================================================================
// Breadcrumb Context
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: React context for managing breadcrumb state, including
//              dynamic breadcrumbs for pages with custom navigation paths,
//              auto-generation from current route, and context updates.
// User Story: US3 (Breadcrumb Navigation)
// ============================================================================

import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BreadcrumbContextValue, Breadcrumb } from '../../types/shell';
import { generateBreadcrumbs } from '../../utils/navigation/RoleBasedMenuFilter';
import { useAuth } from '../../hooks/useAuth';
import type { UserRoleValue } from '@shared/types/user';

/**
 * The context value plus whether a page's custom trail is in force. Both the auto-generated
 * trail and every trail the useBreadcrumb helpers build start with "Home", so the trail's
 * content cannot tell the two apart; only the provider knows.
 */
export interface BreadcrumbContextState extends BreadcrumbContextValue {
  /** True while custom breadcrumbs override the auto-generated trail */
  isCustom: boolean;
}

export const BreadcrumbContext = createContext<BreadcrumbContextState | null>(null);

interface BreadcrumbProviderProps {
  children: React.ReactNode;
}

/**
 * Map auth role (lowercase) to navigation role (capitalized)
 */
function mapAuthRoleToNavRole(authRole: string): UserRoleValue {
  const roleMap: Record<string, UserRoleValue> = {
    'superadmin': 'Admin',
    'admin': 'Admin',
    'manager': 'Manager',
    'operator': 'Operator',
    'user': 'Viewer',
    'viewer': 'Viewer',
  };
  return roleMap[authRole.toLowerCase()] || 'Viewer';
}

/**
 * BreadcrumbProvider component
 * 
 * Features:
 * - Auto-generates breadcrumbs from current route using existing utility
 * - Allows pages to override with custom breadcrumbs
 * - Provides methods to append segments dynamically
 * - Resets to auto-generated breadcrumbs when route changes
 * - Integrates with user role for proper navigation context
 */
export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  
  const [customBreadcrumbs, setCustomBreadcrumbs] = useState<Breadcrumb[] | null>(null);
  const [autoBreadcrumbs, setAutoBreadcrumbs] = useState<Breadcrumb[]>([]);

  // Generate breadcrumbs from current path
  useEffect(() => {
    if (location.pathname && user?.role) {
      try {
        const navRole = mapAuthRoleToNavRole(user.role);
        const generated = generateBreadcrumbs(location.pathname, navRole);
        setAutoBreadcrumbs(generated);
        
        // Reset custom breadcrumbs when route changes
        setCustomBreadcrumbs(null);
      } catch (error) {
        console.error('Error generating breadcrumbs:', error);
        // Fallback breadcrumbs
        setAutoBreadcrumbs([
          { label: 'Home', path: '/', isActive: false },
          { label: 'Current Page', path: location.pathname, isActive: true },
        ]);
      }
    }
  }, [location.pathname, user?.role]);

  // The methods keep their identity across renders, so the useCallback helpers built on
  // them in useBreadcrumb stay stable too.

  // Set custom breadcrumbs (overrides auto-generated)
  const setBreadcrumbs = useCallback((breadcrumbs: Breadcrumb[]) => {
    setCustomBreadcrumbs(breadcrumbs);
  }, []);

  // Append a segment to current breadcrumbs
  const appendBreadcrumb = useCallback((segment: Breadcrumb) => {
    setCustomBreadcrumbs((prevCustom) => {
      const currentBreadcrumbs = prevCustom || autoBreadcrumbs;

      // Make all existing segments inactive
      const updatedBreadcrumbs = currentBreadcrumbs.map(b => ({
        ...b,
        isActive: false,
      }));

      // Add new segment as active
      return [...updatedBreadcrumbs, { ...segment, isActive: true }];
    });
  }, [autoBreadcrumbs]);

  // Reset to auto-generated breadcrumbs
  const resetBreadcrumbs = useCallback(() => {
    setCustomBreadcrumbs(null);
  }, []);

  // Get current breadcrumbs (custom or auto-generated)
  const breadcrumbs = customBreadcrumbs || autoBreadcrumbs;
  const isCustom = customBreadcrumbs !== null;

  const value = useMemo<BreadcrumbContextState>(
    () => ({
      breadcrumbs,
      setBreadcrumbs,
      appendBreadcrumb,
      resetBreadcrumbs,
      isCustom,
    }),
    [breadcrumbs, setBreadcrumbs, appendBreadcrumb, resetBreadcrumbs, isCustom]
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export default BreadcrumbProvider;